import React, { useEffect } from "react";
import styled from "styled-components";
import {
  useTable,
  useFilters,
  usePagination,
  useSortBy,
  useResizeColumns,
  useBlockLayout,
} from "react-table";
// A great library for fuzzy filtering/sorting items
// import matchSorter from "match-sorter";

//import makeData from './MakeData';
import { getValue, setValue } from "@utils/lodash";
import { formatText } from "@_common/format-text";

import ReactTooltip from "react-tooltip";
import { sanitizeInput } from "@_common/dompurify";

//var reactListbox = require("react-listbox");

const Table2Styles = styled.div`
  /* Safety override for any leaked legacy classNames */
  .table .tr .th,
  .table .tr .td {
    padding: 0 !important;
  }

  .table2-wrapper {
    max-height: calc(100vh - 231px);
    overflow-y: hidden;
    overflow-x: auto;
    margin-bottom: 0;
  }

  .table2-root {
    max-width: 100%;
    overflow-x: auto;
    border-spacing: 0;
    border-right: 1px solid #dadce0;
    margin: 0 0 8px;
  }

  .table2-body {
    max-height: calc(100vh - 238px);
    overflow-y: auto;
  }

  .table2-row {
    display: flex;
  }

  .table2-header-cell,
  .table2-body-cell {
    display: flex !important;
    box-sizing: border-box;
    width: 350px;
    justify-content: center;
    flex-direction: column !important;
    margin: 0;
    padding: 0 !important;
    border-bottom: 1px solid #dadce0;
    border-right: 1px solid #dadce0;
    font-size: 12px;
    word-break: break-word;
    position: relative;
  }

  .table2-header-cell {
    color: #2e3151;
    font-size: 14px !important;
    font-weight: 500;
  }

  .table2-body-cell {
    justify-content: start;
  }

  .table2-header-cell:last-child,
  .table2-body-cell:last-child {
    border-right: 0;
  }

  .table2-body-row:nth-child(odd):hover,
  .table2-body-row:nth-child(even):hover {
    background-color: #f8f9fa;
  }

  .table2-resizer {
    display: inline-block;
    width: 1px;
    height: 100%;
    position: absolute;
    right: 0;
    top: 0;
    transform: translateX(50%);
    z-index: 1;
    touch-action: none;
  }

  .table2-resizer.isResizing {
    background: red;
  }
`;

// Define a default UI for filtering
function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length;

  return (
    <input
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(sanitizeInput(e.target.value) || undefined); // Set undefined to remove the filter entirely
      }}
      placeholder={`Search ${count} records...`}
    />
  );
}

// function fuzzyTextFilterFn(rows, id, filterValue) {
//   return matchSorter(rows, filterValue, { keys: [row => row.values[id]] });
// }

// Let the table remove the filter if the string is empty
// fuzzyTextFilterFn.autoRemove = val => !val;

// Our table component
function Table({
  columns,
  data,
  getTableState,
  rowClick,
  // getTrProps,
}) {
  const withoutClassName = (props) => {
    const { className, ...rest } = props || {};
    return rest;
  };

  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      // fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    state,
  } = useTable(
    {
      columns,
      data,
      // defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      initialState: { pageSize: 100 },
      getTableState,
    },
    useFilters,
    useSortBy,
    usePagination,
    useBlockLayout,
    useResizeColumns
  );

  useEffect(() => {
    getTableState(state);
  }, [getValue(state, `selectedRowIds`, [])]);

  return (
    <>
      {/* <Pagination {...paginationProps} /> */}
      <div className="table2-wrapper">
        <div {...withoutClassName(getTableProps())} className="table2-root">
          <div>
            {/* @ts-ignore */}
            {headerGroups.map((headerGroup, index) => {
              return (
                <div
                  {...withoutClassName(headerGroup.getHeaderGroupProps())}
                  className="table2-row table2-header-row"
                >
                  {headerGroup.headers.map((column, i) => {
                    const headerProps = withoutClassName(
                      column.getHeaderProps()
                    );
                    return (
                      <div
                        {...headerProps}
                        className="table2-header-cell"
                        style={{ ...headerProps.style, padding: 0 }}
                        // className={i === 0 ? "th w-auto" : "th"}
                      >
                        {column.render("Header")}
                        {/* Add a sort direction indicator */}
                        {/* Use column.getResizerProps to hook up the events correctly */}
                        {/* @ts-ignore */}
                        {/* Render the columns filter UI */}
                        <div
                          //@ts-ignore
                          {...withoutClassName(
                            column.getResizerProps({ canResize: true })
                          )}
                          className={`table2-resizer ${
                            // @ts-ignore
                            column.isResizing ? "isResizing" : ""
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          <div {...withoutClassName(getTableBodyProps())} className="table2-body">
            {/* @ts-ignore */}
            {rows.map((row, i) => {
              prepareRow(row);
              const rowProps = withoutClassName(row.getRowProps());
              return (
                <div
                  {...rowProps}
                  className="table2-row table2-body-row"
                  onClick={() => rowClick(row)}
                >
                  {/* @ts-ignore */}
                  {row?.cells?.map((cell, key) => {
                    // const rowCellProps =
                    //   key === 0 ? getTrProps(getValue(row, `original`, {})) : {};
                    const cellProps = withoutClassName(
                      cell.getCellProps()
                    );
                    return (
                      <div
                        {...cellProps}
                        className="table2-body-cell"
                        style={{ 
                          ...cellProps.style,
                           padding: 0 }}
                      >
                        {cell.render("Cell")}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* <Pagination {...paginationProps} /> */}
      {/* <br /> */}
      {/* <div className="row">
        <div className="col-xs-6">
          <pre>
            <code>
              Pagination:{" "}
              {JSON.stringify({ pageIndex, pageSize, pageCount }, null, 2)}
            </code>
          </pre>
        </div>
      </div> */}
    </>
  );
}

// Define a custom filter filter function!
function filterGreaterThan(rows, id, filterValue) {
  return rows.filter((row) => {
    const rowValue = row.values[id];
    return rowValue >= filterValue;
  });
}

// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
filterGreaterThan.autoRemove = (val) => typeof val !== "number";

function ScrollableReactTable2(props) {
  const columns = React.useMemo(
    () => [
      {
        Header: "User ID",
        accessor: "userid",
        width: 250,
      },
      {
        Header: "First Name",
        accessor: "firstname",
        width: 250,
      },
      {
        Header: "Last Name",
        accessor: "lastname",
        width: 250,
      },
      {
        Header: "Access Level",
        accessor: "accesslevel",
        width: 250,
      },
      {
        Header: "Added on",
        accessor: "addedon",
        width: 250,
        //width: 60,
      },
      {
        Header: "Last Signed-in",
        accessor: "lastsignedin",
        width: 250,
      },
    ],
    []
  );
  const data = React.useMemo(
    () => [
      {
        id: 1,
        userid:
          "Prabhu@test.com Prabhu@test.com Prabhu@test.com Prabhu@test.com",
        firstname: "Prabhakar",
        lastname: "Murthy",
        accesslevel: "SuperAdmin",
        addedon: "23-May-2021",
        lastsignedin: "23-May-2021",
      },
    ],
    []
  );

  return (
    <Table2Styles>
      <div className="table2-container">
        <Table
          columns={
            getValue(props, `columns.length`, 0) > 0
              ? getValue(props, `columns`, [])
              : columns
          }
          data={
            getValue(props, `data.length`, 0) > 0
              ? getValue(props, `data`, [])
              : data
          }
          getTableState={props?.getTableState}
          rowClick={props.rowClick}
          // getTrProps={props.getTrProps}
          // getTableState={state}
        />
      </div>
    </Table2Styles>
  );
}

export default ScrollableReactTable2;
